import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { API } from '../../App';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import CreateTicketDialog, { TICKET_CATEGORIES } from '../../components/CreateTicketDialog';
import { Ticket, ChevronRight, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_STYLES = {
  open: 'bg-sky-100 text-sky-700',
  in_progress: 'bg-amber-100 text-amber-700',
  resolved: 'bg-emerald-100 text-emerald-700',
  closed: 'bg-slate-200 text-slate-600',
};
const PRIORITY_STYLES = {
  low: 'bg-slate-100 text-slate-600',
  medium: 'bg-blue-100 text-blue-700',
  high: 'bg-orange-100 text-orange-700',
  urgent: 'bg-rose-100 text-rose-700',
};
const CAT_LABEL = Object.fromEntries(TICKET_CATEGORIES.map((c) => [c.value, c.label]));
const fmtStatus = (s) => (s || '').replace('_', ' ');

const StatPill = ({ label, value, color }) => (
  <div className="flex-1 min-w-[120px] bg-white rounded-xl shadow-sm p-4 text-center">
    <div className={`text-2xl font-bold ${color}`}>{value ?? 0}</div>
    <div className="text-xs text-slate-500 capitalize mt-1">{label}</div>
  </div>
);


const TicketsList = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState(null);
  const [filters, setFilters] = useState({ status: 'all', category: 'all', priority: 'all', sla: 'all' });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const query = {};
      Object.entries(filters).forEach(([k, v]) => { if (v !== 'all') query[k] = v; });
      const [tRes, sRes] = await Promise.all([
        axios.get(`${API}/tickets`, { params: query }),
        axios.get(`${API}/tickets/stats`),
      ]);
      setTickets(tRes.data);
      setStats(sRes.data);
    } catch (e) {
      toast.error('Failed to load tickets');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="tickets-list-page">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-4xl font-bold text-slate-900 mb-2">Tickets & Complaints</h1>
          <p className="text-lg text-slate-600">Support tickets, complaints and escalations</p>
        </div>
        <CreateTicketDialog onCreated={load} defaultOpen={params.get('new') === '1'} />
      </div>

      <div className="flex flex-wrap gap-3 mb-6">
        <StatPill label="total" value={stats?.total} color="text-indigo-600" />
        <StatPill label="open" value={stats?.open} color="text-sky-600" />
        <StatPill label="in progress" value={stats?.in_progress} color="text-amber-600" />
        <StatPill label="resolved" value={stats?.resolved} color="text-emerald-600" />
        <StatPill label="closed" value={stats?.closed} color="text-slate-500" />
        <button
          type="button"
          onClick={() => setFilters({ ...filters, sla: filters.sla === 'breached' ? 'all' : 'breached' })}
          className={`flex-1 min-w-[120px] rounded-xl shadow-sm p-4 text-center transition-colors ${
            filters.sla === 'breached' ? 'bg-rose-600 text-white' : 'bg-white hover:bg-rose-50'
          }`}
          data-testid="sla-breached-pill"
        >
          <div className={`text-2xl font-bold ${filters.sla === 'breached' ? 'text-white' : 'text-rose-600'}`}>
            {stats?.sla_breached ?? 0}
          </div>
          <div className={`text-xs mt-1 ${filters.sla === 'breached' ? 'text-rose-100' : 'text-slate-500'}`}>
            overdue &gt;48h
          </div>
        </button>
      </div>

      <div className="flex flex-wrap gap-3 mb-4">
        <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
          <SelectTrigger className="w-40" data-testid="filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            {['open', 'in_progress', 'resolved', 'closed'].map((s) => <SelectItem key={s} value={s}>{fmtStatus(s)}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.category} onValueChange={(v) => setFilters({ ...filters, category: v })}>
          <SelectTrigger className="w-48" data-testid="filter-category"><SelectValue placeholder="Category" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {TICKET_CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.priority} onValueChange={(v) => setFilters({ ...filters, priority: v })}>
          <SelectTrigger className="w-40" data-testid="filter-priority"><SelectValue placeholder="Priority" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Priority</SelectItem>
            {['low', 'medium', 'high', 'urgent'].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.sla} onValueChange={(v) => setFilters({ ...filters, sla: v })}>
          <SelectTrigger className="w-44" data-testid="filter-sla"><SelectValue placeholder="SLA" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All ages</SelectItem>
            <SelectItem value="breached">Overdue (&gt;48h)</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card className="border-0 shadow-lg">
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Ticket className="w-5 h-5 text-indigo-600" />{tickets.length} ticket(s)</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-center text-slate-500 py-8">Loading...</p>
          ) : tickets.length === 0 ? (
            <p className="text-center text-slate-500 py-8">No tickets found.</p>
          ) : (
            <div className="space-y-2">
              {tickets.map((t) => (
                <div
                  key={t.id}
                  onClick={() => navigate(`/staff/tickets/${t.id}`)}
                  className={`flex items-center justify-between gap-3 py-3 px-3 rounded-lg border hover:bg-slate-50 cursor-pointer ${
                    t.sla_breached ? 'border-rose-200 bg-rose-50/40' : 'border-slate-100'
                  }`}
                  data-testid={`ticket-row-${t.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-mono text-slate-400">{t.ticket_number}</span>
                      <span className="font-semibold text-slate-900 truncate">{t.title}</span>
                      {t.escalation_level > 0 && <Badge className="bg-rose-50 text-rose-600">Escalated</Badge>}
                      {t.sla_breached && (
                        <Badge className="bg-rose-600 text-white flex items-center gap-1" data-testid={`sla-badge-${t.id}`}>
                          <AlertTriangle className="w-3 h-3" />
                          Overdue {Math.floor(t.age_hours)}h
                        </Badge>
                      )}
                    </div>
                    <div className="text-sm text-slate-500">{CAT_LABEL[t.category]} • by {t.raised_by_name}{t.assigned_to_name ? ` → ${t.assigned_to_name}` : ''}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={PRIORITY_STYLES[t.priority]}>{t.priority}</Badge>
                    <Badge className={STATUS_STYLES[t.status]}>{fmtStatus(t.status)}</Badge>
                    <ChevronRight className="w-4 h-4 text-slate-400" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default TicketsList;
